import Animated3Dots from '@/components/animations/Animated3Dots';
import AnimatedValidPhone from '@/components/animations/AnimatedValidPhone';
import Btn from '@/components/dataDisplay/Btn';
import BubbleTimer from '@/components/dataDisplay/BubbleTimer';
import ExternalLink from '@/components/dataDisplay/ExternalLink';
import UniversalCookiesManager from '@/modules/core/cookiesManager/UniversalCookiesManager';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { SerializableStudentSolutionContext } from '@/modules/core/data/types/SerializableStudentSolutionContext';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import I18nLink from '@/modules/core/i18n/components/I18nLink';
import useI18n, { I18n } from '@/modules/core/i18n/hooks/useI18n';
import { encodeJSONToQueryParameter } from '@/modules/core/js/url';
import { QUERY_SIMULATION_KEY } from '@/modules/solutions/solutionsFilters';
import { serializeStudentSolutionContext } from '@/modules/solutions/studentSolutionContext';
import {
  Amplitude,
  LogOnMount,
} from '@amplitude/react-amplitude';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import map from 'lodash.map';
import size from 'lodash.size';
import React, {
  Fragment,
  useState,
} from 'react';
import {
  Trans,
  useTranslation,
} from 'react-i18next';

type Props = {
  chatbotContext: SerializableStudentSolutionContext;
  studentSolutionsFilteredByContext: StudentSolution[];
  className?: string;
  isInIframe: boolean;
}

/**
 * Display the last part of the Chatbot.
 *
 * Indicates how many solutions were found for the user context.
 * Displays a button that redirects to the Solutions page (link embeds the serialized context within)
 */
const SolutionsFoundCard: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    chatbotContext,
    studentSolutionsFilteredByContext,
    className,
    isInIframe,
  } = props;
  const { t } = useTranslation();
  const { locale }: I18n = useI18n();
  const solutionCount = size(studentSolutionsFilteredByContext);
  const [isLoadingSolutionsPage, setIsLoadingSolutionsPage] = useState(false);
  const solutionsPageLink = `/solutions?${QUERY_SIMULATION_KEY}=${encodeJSONToQueryParameter(serializeStudentSolutionContext(chatbotContext))}`; // TODO sentry warning if < 2k chars https://stackoverflow.com/questions/417142/what-is-the-maximum-length-of-a-url-in-different-browsers

  // See https://github.com/vercel/next.js/issues/10305
  // See https://nextjs.org/docs/api-reference/next/link#if-the-child-is-a-function-component
  // @ts-ignore
  // eslint-disable-next-line react/display-name
  const SolutionsPageButton = React.forwardRef(({ onClick, props }, ref) => {
    return (
      <Btn
        ref={ref}
        onClick={(e): void => {
          // Stores the selected persona in the browser localStorage
          const universalCookiesManager = new UniversalCookiesManager();
          universalCookiesManager.patchUserData({ persona: chatbotContext.persona });

          // Starts button's animation
          setIsLoadingSolutionsPage(true);

          if (onClick) {
            onClick(e);
          }
        }}
        {...props}
      >
        <FontAwesomeIcon icon="eye" />
        {t('chatbotPage.solutionsFound.button', 'Voir les solutions')}
        {
          isLoadingSolutionsPage ? (
            <Fragment>
              {' '}<Animated3Dots />
            </Fragment>
          ) : null
        }
      </Btn>
    );
  });

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
        solutionsFound: {
          count: solutionCount,
          ref: map(studentSolutionsFilteredByContext, 'ref'),
        },
      })}
    >
      <LogOnMount eventType="chatbot-solutions-displayed" />

      <BubbleTimer duration={2000} style={{ paddingBottom: 300 }}>
        <div className={classnames('text-spacing', className)}>
          <AnimatedValidPhone />
          <div>
            <Trans
              i18nKey={'chatbotPage.solutionsFound.text'}
              count={solutionCount}
            >
              Nous avons trouvé {{ count: solutionCount }} solution pour vous.
            </Trans>
          </div>
          <div>
            {
              // XXX When displayed within an iframe, then open the link in a new page
              //  The point is to avoid displaying too much on the institutional website,
              //  but rather redirect the user to our platform so that they may benefit from all our services
              isInIframe ? (
                <ExternalLink
                  href={'/' + locale + solutionsPageLink}
                >
                  <SolutionsPageButton />
                </ExternalLink>
              ) : (
                <I18nLink
                  href={solutionsPageLink}
                >
                  <SolutionsPageButton />
                </I18nLink>
              )
            }
          </div>
        </div>
      </BubbleTimer>
    </Amplitude>
  );
};

export default SolutionsFoundCard;

